import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SSOApiService from "../../../services/SsoServices";
import {
  MY_CONTENT_EXISTS_SUSBCRIPTION,
  ADD_TO_CART,
  STOP_SUBSCRIBER_SUBSCRIPTION,
} from "../../../constants/api-constants";
import "./purchase.css";

/*istanbul ignore next */
const SubscriptionDetail = ({ subscription, onBack, onUpdate }) => {
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getFrequencyLabel = (frequencyType) => {
    const frequencies = {
      1: "Daily",
      2: "Weekly",
      3: "Monthly",
      4: "Quarterly",
      5: "Yearly",
      99: "One Time",
    };
    return frequencies[frequencyType] || "Unknown";
  };

  const isApproachingEndDate = () => {
    const endDate = new Date(subscription.endDate || subscription.egEndDate);
    const today = new Date();
    const daysUntilEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilEnd <= 15 && daysUntilEnd > 0;
  };

  const shouldShowRenewButton = () => {
    return (
      isApproachingEndDate() &&
      !subscription.isRecurring &&
      subscription.isRenewAllowed &&
      !subscription.isAlreadyRenewed &&
      !subscription.isStopped &&
      !subscription.isEgStopped
    );
  };

  const handleUnsubscribe = async () => {
    try {
      setIsProcessing(true);
      let userId = null;
      try {
        const currentUser = localStorage.getItem("currentUser");
        if (currentUser) {
          const userData = JSON.parse(currentUser);
          userId = userData?.result?.user?.user?.userId;
        }
      } catch (error) {
        console.error("Failed to get user data:", error);
      }
      const response = await SSOApiService.ssoPost(
        STOP_SUBSCRIBER_SUBSCRIPTION,
        {
          userId: userId,
          requestId: subscription.requestId,
        },
      );

      if (response?.responseStatus?.isSuccess) {
        setShowUnsubscribeModal(false);
        onUpdate();
        onBack();
      } else {
        alert(
          response?.responseStatus?.description ||
            "Failed to unsubscribe. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error unsubscribing:", error);
      alert("Failed to unsubscribe. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRenew = async () => {
    try {
      setIsProcessing(true);

      // Step 1: Fetch subscription content list
      const contentResponse = await SSOApiService.ssoGet(
        MY_CONTENT_EXISTS_SUSBCRIPTION,
      );

      if (!contentResponse?.responseStatus?.isSuccess) {
        alert(
          contentResponse?.responseStatus?.description ||
            "Failed to fetch subscription content. Please try again.",
        );
        setIsProcessing(false);
        return;
      }

      // Step 2: Find the matching content by contentId
      const contentsList = contentResponse?.result?.contents || [];
      const matchingContent = contentsList.find(
        (content) => content.contentId === subscription.contentId,
      );

      if (!matchingContent) {
        alert("Subscription content not found. Please try again.");
        setIsProcessing(false);
        return;
      }

      // Step 3: Get available subscriptions
      const subscriptions = matchingContent.subscriptions || [];

      if (subscriptions.length === 0) {
        alert("No subscription plans available for this content.");
        setIsProcessing(false);
        return;
      }

      // If only one subscription, add directly to cart
      if (subscriptions.length === 1) {
        await addToCartAndRedirect(
          matchingContent.contentId,
          subscriptions[0].frequency,
        );
      } else {
        // Multiple subscriptions, show selection modal
        setAvailableSubscriptions(subscriptions);
        setSelectedSubscriptionPlan(subscriptions[0].frequency);
        setShowSubscriptionModal(true);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Error renewing subscription:", error);
      alert("Failed to renew subscription. Please try again.");
      setIsProcessing(false);
    }
  };

  const addToCartAndRedirect = async (contentId, frequency) => {
    try {
      setIsProcessing(true);

      const addToCartPayload = {
        itemId: contentId,
        quantity: 1,
        transactionType: 2,
        frequency: frequency,
        currency: "USD",
      };

      const cartResponse = await SSOApiService.ssoPost(
        ADD_TO_CART,
        addToCartPayload,
      );

      if (cartResponse?.responseStatus?.isSuccess) {
        setShowSubscriptionModal(false);
        window.location.href = "/purchase/cart";
      } else {
        alert(
          cartResponse?.responseStatus?.description ||
            "Failed to add to cart. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add to cart. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmRenew = async () => {
    if (selectedSubscriptionPlan) {
      await addToCartAndRedirect(
        subscription.contentId,
        selectedSubscriptionPlan,
      );
    }
  };

  return (
    <div className="subscription-detail-container">
      <div className="subscription-detail-header">
        <IconButton onClick={onBack} className="back-button">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" className="subscription-detail-title">
          {subscription.isEgSubscription
            ? subscription.brandName
            : subscription.subscriptionName}
        </Typography>
      </div>

      <div className="subscription-detail-content">
        <div className="detail-field">
          <Typography variant="subtitle2" className="detail-label">
            Subscription Name
          </Typography>
          <input
            type="text"
            value={
              subscription.isEgSubscription
                ? subscription.brandName
                : subscription.subscriptionName
            }
            disabled
            className="detail-input-field"
          />
        </div>

        <div className="detail-field">
          <Typography variant="subtitle2" className="detail-label">
            Distributor
          </Typography>
          <input
            type="text"
            value={subscription.distBrandName || "N/A"}
            disabled
            className="detail-input-field"
          />
        </div>

        <div className="detail-field">
          <Typography variant="subtitle2" className="detail-label">
            Frequency
          </Typography>
          <input
            type="text"
            value={getFrequencyLabel(subscription.frequencyType)}
            disabled
            className="detail-input-field"
          />
        </div>

        <div className="detail-field">
          <Typography variant="subtitle2" className="detail-label">
            Start Date
          </Typography>
          <input
            type="text"
            value={formatDate(subscription.startDate)}
            disabled
            className="detail-input-field"
          />
        </div>

        <div className="detail-field">
          <Typography variant="subtitle2" className="detail-label">
            End Date
          </Typography>
          <input
            type="text"
            value={formatDate(subscription.endDate || subscription.egEndDate)}
            disabled
            className="detail-input-field"
          />
        </div>

        <div className="detail-field">
          <div className="detail-label-with-icon">
            <Typography variant="subtitle2" className="detail-label">
              Subscription Fees/Taxes
            </Typography>
            <Tooltip title="Includes applicable taxes calculated during checkout">
              <InfoOutlinedIcon fontSize="small" className="info-icon" />
            </Tooltip>
          </div>
          <input
            type="text"
            value={`$${subscription.subscriptionFees?.toFixed(2) || "0.00"}`}
            disabled
            className="detail-input-field"
          />
        </div>

        <div className="detail-field-checkbox">
          <FormControlLabel
            control={<Checkbox checked={subscription.isRecurring || false} />}
            label="Auto Renewal"
            style={{ cursor: "default", pointerEvents: "none" }}
          />
        </div>

        <div className="subscription-actions">
          {shouldShowRenewButton() && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleRenew}
              disabled={isProcessing}
              className="renew-button"
            >
              {isProcessing ? "PROCESSING..." : "RENEW"}
            </Button>
          )}
          {!subscription.isStopped && !subscription.isEgStopped && (
            <Button
              variant="contained"
              onClick={() => setShowUnsubscribeModal(true)}
              className="unsubscribe-button"
            >
              UNSUBSCRIBE
            </Button>
          )}
        </div>
      </div>

      {/* Unsubscribe Confirmation Modal */}
      <Dialog
        open={showUnsubscribeModal}
        onClose={() => !isProcessing && setShowUnsubscribeModal(false)}
        className="unsubscribe-dialog"
      >
        <DialogTitle>
          <div className="dialog-icon">📋</div>
          Are you sure you want to Unsubscribe?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" align="center">
            Your subscription will not renew in the next billing cycle.
          </Typography>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button
            onClick={() => setShowUnsubscribeModal(false)}
            disabled={isProcessing}
            variant="outlined"
          >
            CANCEL
          </Button>
          <Button
            onClick={handleUnsubscribe}
            disabled={isProcessing}
            variant="contained"
            color="primary"
          >
            {isProcessing ? "PROCESSING..." : "UNSUBSCRIBE"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Selection Modal */}
      <Dialog
        open={showSubscriptionModal}
        onClose={() => !isProcessing && setShowSubscriptionModal(false)}
        className="subscription-selection-dialog"
      >
        <DialogTitle>
          <div className="dialog-icon">🔄</div>
          Select Subscription Plan
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            align="center"
            style={{ marginBottom: "16px" }}
          >
            Choose the subscription plan you want to renew:
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedSubscriptionPlan || ""}
              onChange={(e) => setSelectedSubscriptionPlan(e.target.value)}
              displayEmpty
            >
              {availableSubscriptions.map((sub) => (
                <MenuItem key={sub.contentSubscriptionId} value={sub.frequency}>
                  {getFrequencyLabel(sub.frequency)} - $
                  {sub.actualValue?.toFixed(2) ||
                    sub.baseValue?.toFixed(2) ||
                    "0.00"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button
            onClick={() => setShowSubscriptionModal(false)}
            disabled={isProcessing}
            variant="outlined"
          >
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmRenew}
            disabled={isProcessing || !selectedSubscriptionPlan}
            variant="contained"
            color="primary"
          >
            {isProcessing ? "PROCESSING..." : "ADD TO CART"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

SubscriptionDetail.propTypes = {
  subscription: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default SubscriptionDetail;
